import { NavigateFunction } from 'react-router-dom';
import { PaginationParams } from 'enums/paginationParams';
import { getPaginationState } from 'utils/local.storage.utils';

export const buildUrlWithStoredTableState = (route: string, storageKey?: string): string => {
  if (!storageKey) {
    return `/${route}`;
  }

  const storedPagination = getPaginationState(storageKey);

  if (!storedPagination) {
    return `/${route}`;
  }

  const params = new URLSearchParams();

  if (storedPagination.page) {
    params.set(PaginationParams.Page, storedPagination.page.toString());
  }

  if (storedPagination.pageSize) {
    params.set(PaginationParams.PageSize, storedPagination.pageSize.toString());
  }

  if (storedPagination.filters?.length) {
    const filtersString = storedPagination.filters
      .map(
        filter =>
          `${filter.columnId}${filter.condition ? `[${filter.condition}]` : ''}:${filter.value}`,
      )
      .join('~');
    params.set(PaginationParams.Filter, filtersString);
  }

  if (storedPagination.sortBy?.length) {
    const sortByString = storedPagination.sortBy
      .map(sort => `${sort.columnId}:${sort.order}`)
      .join(',');
    params.set(PaginationParams.SortBy, sortByString);
  }

  const queryString = params.toString();
  return `/${route}${queryString ? `?${queryString}` : ''}`;
};

export const navigateWithStoredTableState = (
  navigate: NavigateFunction,
  route: string,
  storageKey?: string,
): void => {
  const urlWithState = buildUrlWithStoredTableState(route, storageKey);
  navigate(urlWithState);
};

export const ROUTE_STORAGE_KEY_MAP: Record<string, string> = {
  'competition-hierarchy/sports': 'competition-hierarchy/sports',
  'competition-hierarchy/organizations': 'organizations',
  'competition-hierarchy/competitions': 'competitions',
  'competition-hierarchy/seasons': 'competition-hierarchy/seasons',
  'competition-hierarchy/rounds': 'competition-hierarchy/rounds',
  'competition-hierarchy/groups': 'groups',
  'competition-hierarchy/clubs': 'clubs',
  'competition-hierarchy/fixtures': '/competition-hierarchy/fixtures',
  'competition-hierarchy/venues': 'competition-hierarchy/venues',
  'competition-hierarchy/localities': 'competition-hierarchy/localities',
  'competition-hierarchy/persons': 'persons',
  'competition-hierarchy/competitors/teams': 'competition-hierarchy/competitors/teams',
  'competition-hierarchy/competitors/players': 'competition-hierarchy/competitors/players',
  'competition-hierarchy/competitors/doubles': 'competitors/doubles',
  'competition-hierarchy/competitors/horses': 'competition-hierarchy/horses',
  'competition-hierarchy/competitors/tbds': 'competition-hierarchy/competitors/tbds',

  'proposals/proposal/ingestion/fixtures': 'ixtures',
  'proposals/proposal/ingestion/squads': 'quads',
  'proposals/proposal/ingestion/fixturesquads': 'ixtures',
  'proposals/proposal/ingestion/competitions': 'ompetitions',

  'proposals/proposal/create/fixtures': 'proposals/proposal/create/fixtures',
  'proposals/proposal/create/players': 'proposals/proposal/create/players',
  'proposals/proposal/create/doubles': 'proposals/proposal/create/doubles',
  'proposals/proposal/create/teams': 'proposals/proposal/create/teams',
  'proposals/proposal/create/contracts': 'proposals/proposal/create/contracts',
  'proposals/proposal/create/hierarchy': 'proposals/proposal/create/hierarchy',
  'proposals/proposal/update': 'proposals/proposal/update',
  'proposals/proposal/delete': 'proposals/proposal/delete',
  'proposals/proposedchanges': 'sourceRequests',
  'proposals/changesLog': 'changesLog',
  'proposals/dynamicCadence': 'external-etl/sync/dynamic-cadence',

  'settings/users': 'settings/useraccess',
  'settings/sources': 'settings/sources',
  'settings/systeminfo': 'settings/systeminfo',
  'settings/matching-sources': 'sources',

  'matching/pendingmatchrequests/fixtures': 'matchrequests/fixtures',
  'matching/pendingmatchrequests/teams': 'matchrequests/teams',
  'matching/pendingmatchrequests/players': 'matchrequests/players',
  'matching/pendingmatchrequests/other': 'matchrequests/undefined',

  'matching/sports': 'matching-sports',
  'matching/organizations': 'matching-organizations',
  'matching/clubs': 'matching-clubs',
  'matching/conferences': 'matching-conferences',
  'matching/divisions': 'matching-divisions',
  'matching/competitions': 'matching-competitions',
  'matching/seasons': 'matching-seasons',
  'matching/rounds': 'matching-rounds',
  'matching/fixtures': 'matching-fixtures',
  'matching/venues': 'matching-venues',
  'matching/localities': 'matching-localities',
  'matching/persons': 'matching-persons',
  'matching/competitors/teams': 'matching-competitors/teams',
  'matching/competitors/players': 'matching-competitors/players',
  'matching/competitors/doubles': 'matching-competitors/doubles',
  'matching/competitors/horses': 'matching-competitors/horses',

  'competitors/tbds': 'competitors/tbds',
};

export const getStorageKeyForRoute = (route: string): string | undefined =>
  ROUTE_STORAGE_KEY_MAP[route];
